import { action, computed, observable } from 'mobx';
import isPlainObject from 'lodash/isPlainObject';

import { modalStore } from '~/common/stores/ui/modal';
import { loginStore } from '~/common/stores/ui/loginStore';
import { offerSuiteStore } from '~/common/stores/domain/offerSuite';
import { promoCodeStore } from '~/common/stores/domain/promoCode';

import userInfo from '~/common/services/userInfo';
import userNumber from '~/common/services/userNumber';
import offerDetails from '~/common/services/offerDetails';
import convertToAnnual from '~/common/services/convertToAnnual';
import { isBoolean, isObject } from '~/common/utils/typeCheckers';
import { PLAN_TERM_ANNUAL, OFFER_SUITE_ANNUAL, LMI_LOGIN_FLOW_ENABLED, OFFER_SUITE_WITH_TRUE_SOLO } from '~/common/utils/consts';
import deepGet from '~/common/utils/deepGet';
import { gtm, purchaseEvent, completeBuyFlowEvent, errorPurchaseEvent } from '~/common/utils/tracking';

class AnnualUpgradeClass {
    @observable offerSuite;
    @observable currentOfferDetails;
    @observable upgradeOfferDetails;
    @observable fetchingUserInfo = false;
    @observable userAlreadyAnnual = false;
    @observable errorUpgradingToAnnual = false;
    @observable userRejectedUpgrade = false;
    @observable userUpgradedSuccesfully = false;
    @observable upgradingUserToAnnual = false;
    @observable trialHasOldPrices = false;

    @action setCurrentOfferDetails(obj) {
        this.currentOfferDetails = isObject(obj, this.currentOfferDetails);
    }

    @action setUpgradeOfferDetails(obj) {
        this.upgradeOfferDetails = isObject(obj, this.upgradeOfferDetails);
    }

    @action setFetchingUserInfo(bool) {
        this.fetchingUserInfo = isBoolean(bool, this.fetchingUserInfo);
    }

    @action setUserAlreadyAnnual(bool) {
        this.userAlreadyAnnual = isBoolean(bool, this.userAlreadyAnnual);
    }

    @action setErrorUpgradingToAnnual(bool) {
        this.errorUpgradingToAnnual = isBoolean(bool, this.errorUpgradingToAnnual);
    }

    @action setUserRejectedUpgrade(bool) {
        this.userRejectedUpgrade = isBoolean(bool, this.userRejectedUpgrade);
    }

    @action setUserUpgradedSuccesfully(bool) {
        this.userUpgradedSuccesfully = isBoolean(bool, this.userUpgradedSuccesfully);
    }

    @action setUpgradingUserToAnnual(bool) {
        this.upgradingUserToAnnual = isBoolean(bool, this.upgradingUserToAnnual);
    }

    @action setTrialHasOldPrices(bool) {
        this.trialHasOldPrices = isBoolean(bool, this.trialHasOldPrices);
    }

    @action authUser() {
        // set active search to true
        this.setFetchingUserInfo(true);
        loginStore.authUser();
    }

    @action validateUser(code, callBack) {
        // get an auth token and then fetch user info
        loginStore.validateUser(code, () => {
            this.getUserInfo(null, true);
            if (callBack) callBack();
        }, false, true);
    }

    @action getUserInfo(callBack, noLoginNeeded) {
        // Utilizes auth user data to set user numbers and plan prices
        const setUserData = (data) => {
            userNumber(loginStore.userInfo.VpsId, loginStore.authToken).then((rsp) => {
                loginStore.setUserNumbers(rsp.data);
            });

            // date the user signed up for Trial
            const signupDate = new Date(data.ActivationDate).getTime();
            // New Prices Date
            const newPricesDate = new Date('January 11, 2023 00:00:00Z').getTime();
            // if the Trial signup date is before the new prices start date
            if (signupDate < newPricesDate) {
                // Set Trial has old prices flag
                this.setTrialHasOldPrices(true);
            }

            // if the user's offersuite is not the same as the prefetched one fetch new offer suite
            if (loginStore.userOfferSuiteID !== offerSuiteStore.offerSuiteId && loginStore.userOfferSuiteID) {
                offerSuiteStore.getOfferSuite(loginStore.userOfferSuiteID, this.getOffersDetails());
            } else {
                offerSuiteStore.getOfferSuite(this.trialHasOldPrices ? OFFER_SUITE_ANNUAL : OFFER_SUITE_WITH_TRUE_SOLO, this.getOffersDetails());
            }

            // close log in modal
            if (modalStore.childComponentString === 'loginModal') modalStore.setModalOptions({ isModalOpen: false });
            if (callBack) callBack();
        };

        if (noLoginNeeded) {
            setUserData(loginStore.userInfo);
        } else {
            // set active search to true
            this.setFetchingUserInfo(true);

            // get user info
            userInfo(loginStore.authToken).then((resp) => {
                const { data } = resp;
                // if error from the API
                if (data.Success === false) throw new Error('failed user info API');
                // save user info
                loginStore.setUserInfo(data);
                setUserData(data);
            }).catch((err) => {
                throw err;
            });
        }
    }

    @action resetFlow() {
        // set time out error
        loginStore.setLoginError('your session has expired please log in again');
        // open log in modal
        const modalFeatureType = LMI_LOGIN_FLOW_ENABLED ? 'loginLmiModal' : 'loginModal';
        modalStore.setModalOptions({
            childComponentString: modalFeatureType,
            isModalClosable: false,
            modalType: 'xlarge',
            isModalOpen: true
        });
    }

    @action getOffersDetails() {
        const { authToken, userVpsId, userOfferId } = loginStore;
        const promo = promoCodeStore.annualUpgradeCoupon;

        offerDetails(authToken, userVpsId, userOfferId, promo).then((resp) => {
            const { data } = resp;
            // if invalid response or success === false throw an error
            if (!isPlainObject(data) || (data.success && data.success === false)) throw new Error('failed to fetch offer details');

            // if user's current plan is annual stop them and  set flag
            if (data.term && data.term.toLowerCase() === PLAN_TERM_ANNUAL) throw new Error('user already in annual plan');
            // save offer details
            this.setCurrentOfferDetails(resp.data);

            // find matching annual offer
            if (!this.matchingAnnualPlan) throw new Error('no matching annual plan');

            // get annual offer details
            offerDetails(authToken, userVpsId, this.matchingAnnualPlan.Id, promo).then((rsp) => {
                const { data: dta } = rsp;
                if (!isPlainObject(dta) || (dta.success && dta.success === false)) throw new Error('failed to fetch offer details');

                this.setUpgradeOfferDetails(dta);
            }).catch((err) => {
                // reset user to try again
                this.resetFlow();
                return err;
            }).then(() => {
                this.setFetchingUserInfo(false);
            });
        }).catch((err) => {
            // if user is already annual
            if (err.message === 'user already in annual plan') {
                this.setUserAlreadyAnnual(true);
            } else {
                // reset user to try again
                this.resetFlow();
                this.setFetchingUserInfo(false);
            }
        }).then(() => {
            this.setFetchingUserInfo(false);
        });
    }

    @action convertToAnnual() {
        // set converting flag
        this.setUpgradingUserToAnnual(true);
        // clear previous errors if any
        if (this.errorUpgradingToAnnual) this.setErrorUpgradingToAnnual(false);
        // get user info
        const { authToken, userVpsId, userOfferSuiteId } = loginStore;
        // get promocode
        const promo = promoCodeStore.annualUpgradeCoupon;
        // call API
        convertToAnnual(authToken, userVpsId, userOfferSuiteId, this.matchingAnnualPlan.Id, promo).then((resp) => {
            const success = deepGet(resp, ['data', 'Success']);

            if (success !== true) {
                gtm(errorPurchaseEvent(resp.data.ErrorText, 'upgrade'));
                throw new Error('failed to upgrade');
            }
            gtm(purchaseEvent(resp.data, 'upgrade'));
            gtm(completeBuyFlowEvent(resp.data, 'upgrade'));

            this.setUserUpgradedSuccesfully(true);
        }).catch(() => {
            this.setErrorUpgradingToAnnual(true);
        }).then(() => {
            this.setUpgradingUserToAnnual(false);
        });
    }

    @computed get currentOfferDetailsAddons() {
        if (!this.currentOfferDetails || !this.currentOfferDetails.chargeEstimates || this.currentOfferDetails.chargeEstimates.length < 0) return false;

        return this.currentOfferDetails.chargeEstimates;
    }

    @computed get upgradeOfferDetailsAddons() {
        if (!this.upgradeOfferDetails || !this.upgradeOfferDetails.chargeEstimates || this.upgradeOfferDetails.chargeEstimates.length < 0) return false;

        return this.upgradeOfferDetails.chargeEstimates;
    }

    @computed get matchingAnnualPlan() {
        if (!offerSuiteStore || !offerSuiteStore.offerSuite) return false;
        return offerSuiteStore.offerSuite.Offers.find((offer) => {
            return offer.Name === this.currentOfferDetails.name && offer.Term.toLowerCase() === PLAN_TERM_ANNUAL;
        });
    }

    @computed get userInfoFetched() {
        return this.currentOfferDetails && this.upgradeOfferDetails;
    }

    @computed get userUpgradedOrRejected() {
        return this.userRejectedUpgrade || this.userUpgradedSuccesfully;
    }

    @computed get totalSavingsForUpgrading() {
        // check that the offers have been fetched and savings are present
        if (!this.currentOfferDetails || !this.currentOfferDetails.totalAmount || typeof this.currentOfferDetails.totalAmount !== 'number') return false;
        if (!this.upgradeOfferDetails || !this.upgradeOfferDetails.totalAmount || typeof this.upgradeOfferDetails.totalAmount !== 'number') return false;
        // get ruby details
        const rubyDetail = this.upgradeOfferDetails.chargeEstimates.find(addOn =>
            addOn.amount > 0 && addOn.chargeType === 'Feature');
        const digitalListingAddon = this.currentOfferDetails.chargeEstimates.find(addon => addon.name === 'Digital Listing Add-on') || {};

        // add 11 more months to the annual amount due today
        const annualTotal = rubyDetail ? this.upgradeOfferDetails.totalAmount + (rubyDetail.amount * 11) + (digitalListingAddon?.amount || 0) : this.upgradeOfferDetails.totalAmount + (digitalListingAddon?.amount || 0);
        // multiply monthly charges by 12 and substract from annual charges
        const totalSavings = (this.currentOfferDetails.totalAmount * 12) - ((digitalListingAddon?.amount || 0) * 11) - annualTotal;
        // if no savings return
        if (totalSavings < 0) return false;

        return totalSavings.toFixed(2);
    }
}

const annualUpgradeStore = new AnnualUpgradeClass();
export { annualUpgradeStore, AnnualUpgradeClass };
